import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import {
  createApi, fetchBaseQuery, FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import SLDParser from 'geostyler-sld-parser';
import { Style as GeoStylerStyle } from 'geostyler-style';
import { Style } from 'util';
import { createLogger } from 'services/debugLogger';
import { GEOSERVER_API, GEOSERVER_API_REST } from '../../configs/endpoints';
import {
  ComplexLayerDto, LayersDto,
  StyleDto,
  StylesDto, WorkspacesDto,
} from './dto';
import ALLOWED_WORKSPACES from '../../configs/workspaces';
import { GEOSERVER_BEARER } from '../../configs/credentials';
import { GeoLayer, Workspace } from '../../types/geoserver.types';
import { connectionTimeout } from '../../configs/initialValues';

const logger = createLogger('geoserver');

const api = createApi({
  reducerPath: 'geoserver-api',
  baseQuery: fetchBaseQuery({
    baseUrl: GEOSERVER_API,
    timeout: connectionTimeout,
    prepareHeaders: (headers) => {
      headers.set('Authorization', GEOSERVER_BEARER);
      return headers;
    },
  }),
  tagTypes: ['Style'],
  endpoints: (builder) => ({
    getWorkspaces: builder.query<Workspace[] | [], {}>({
      queryFn: async (_arg, _queryApi, _extraOptions, fetchWithBQ) => {
        // get workspaces
        const rawWorkspaces = await fetchWithBQ('rest/workspaces') as QueryReturnValue<WorkspacesDto>;
        if (rawWorkspaces.data) {
          const workspaces = {
            data: {
              workspaces: {
                workspace: rawWorkspaces.data.workspaces.workspace
                  .filter((workspace) => ALLOWED_WORKSPACES.includes(workspace.name)),
              },
            },
          };
          // get layerGroups
          const layersP = workspaces.data.workspaces.workspace.map((w) => (fetchWithBQ(`rest/workspaces/${w.name}/layers`) as PromiseLike<QueryReturnValue<LayersDto>>));
          const result = await Promise.all(layersP).then(
            async (layers:QueryReturnValue<LayersDto>[]) => {
              const map = layers.map(async (layerDto, index) => {
                if (layerDto.data?.layers) {
                  const complexLayerP = layerDto.data?.layers
                    .layer.map(async (layer) : Promise<GeoLayer | null> => {
                      const layerRes = await fetchWithBQ(
                        `rest/workspaces/${workspaces.data?.workspaces.workspace[index].name}/layers/${layer.name}`,
                      ) as QueryReturnValue<ComplexLayerDto>;

                      if (!layerRes.data?.layer) return null;

                      let unit: string | undefined;

                      const resourceHref = layerRes.data.layer.resource?.href;
                      if (resourceHref) {
                        const pathIndex = resourceHref.indexOf('/geoserver/rest');
                        const parsedResourceHref = GEOSERVER_API_REST + resourceHref.slice(pathIndex + '/geoserver/rest'.length);

                        if (parsedResourceHref) {
                        // eslint-disable-next-line max-len
                          const resourceRes = await fetchWithBQ(parsedResourceHref) as QueryReturnValue<any>;
                          unit = resourceRes?.data?.coverage?.dimensions?.coverageDimension?.unit;
                        }
                      }

                      return {
                        ...layerRes.data.layer,
                        unit,
                      };
                    });
                  const r: Workspace = await Promise.all(complexLayerP)
                    .then((complexLayers) => complexLayers
                      .reduce<Workspace>((acc, complexLayer) => {
                        if (complexLayer) {
                          acc.layers?.push({
                            ...complexLayer,
                            unit: complexLayer.unit,
                          });
                        }
                        return acc;
                      }, {
                        name: workspaces.data.workspaces.workspace[index].name,
                        href: workspaces.data.workspaces.workspace[index].href,
                        layers: [],
                      }));
                  return r;
                }
                return {};
              });

              const res = await Promise.all(map).then();
              return res as Workspace[];
            },
          );

          return { data: result.filter((r) => r.name) };
        }

        return { error: rawWorkspaces.error as FetchBaseQueryError };
      },
    }),
    getStyles: builder.query<GeoStylerStyle[] | [], { workspace?: string }>({
      queryFn: async (_arg, _queryApi, _extraOptions, fetchWithBQ) => {
        const workspace = _arg.workspace || ALLOWED_WORKSPACES[0];
        const parser = new SLDParser();

        // get styles
        const response = await (fetchWithBQ(`rest/workspaces/${workspace}/styles`) as PromiseLike<QueryReturnValue<StylesDto>>);

        if (response.data) {
          if (response.data.styles) {
            const stylesP = response.data.styles.style.map((s) => (fetchWithBQ({
              url: `rest/workspaces/${workspace}/styles/${s.name}.sld`,
              responseHandler: (response: { text: () => any }) => response.text(),
            }) as PromiseLike<any>));

            const complexStylesP = await Promise.all(stylesP)
              .then(async (styles) => styles.map(async (style) => {
                const parse = await parser.readStyle(style.data);
                return parse.output;
              }));

            const styles = await Promise.all(complexStylesP);
            return { data: styles as GeoStylerStyle[] };
          }
          return { data: [] };
        }

        return { error: response.error as FetchBaseQueryError };
      },
    }),
    getLayerStyles: builder.query<(GeoStylerStyle & {deletable: boolean})[], {layerName: string}>({
      providesTags: (result, error, arg) => (result
        ? [...result.map(({ name }) => ({ type: 'Style' as const, name })), 'Style']
        : ['Style']),
      query: ({ layerName }) => ({
        url: `rest/layers/${layerName}/styles`,
        responseHandler: async (response: any/* : { text: () => any } */) => {
          const parser = new SLDParser();
          const res = JSON.parse(await response.text()) as StylesDto;

          const stylesP = res.styles.style.map((r: StyleDto) => {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), connectionTimeout);

            return fetch(`${GEOSERVER_API}/rest/styles/${r.name}.sld`, {
              headers: { Authorization: GEOSERVER_BEARER },
              signal: controller.signal,
            })
              .then((res) => {
                clearTimeout(timeoutId);
                return res.text();
              })
              .catch((error) => {
                clearTimeout(timeoutId);
                throw error;
              });
          });

          const result = await Promise
            .all(stylesP)
            .then(async (styles) => {
              const sP = await Promise.all(styles.map(async (style) => {
                const parse = await parser.readStyle(style);
                return { ...parse.output, deletable: style.includes('deletable=true') };
              }));
              return sP.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            });
          return result;
        },
      }),
    }),
    updateLayerStyle: builder.mutation<string, {
      workspace: string, style: string, rawStyle: string}>({
        invalidatesTags: (result, error, arg) => [{ type: 'Style', id: arg.style.substring(arg.style.indexOf(':') + 1) }],
        query: ({ workspace, style, rawStyle }) => ({
          url: `rest/styles/${style.substring(style.indexOf(':') + 1)}.sld`,
          method: 'PUT',
          body: rawStyle,
          headers: { 'Content-Type': 'application/vnd.ogc.sld+xml' },

        }),

      }),
    createLayerStyle: builder.mutation<string, {
      layer: string, rawStyle: string, styleName: string}>({
        invalidatesTags: ['Style'],
        query: ({ layer, rawStyle, styleName }) => ({
          url: 'rest/styles',
          method: 'POST',
          body: rawStyle,
          headers: { 'Content-Type': 'application/vnd.ogc.sld+xml' },
          async responseHandler(response) {
            if (response) {
              const r = styleName;
              const body = JSON.stringify({ style: { name: r, filename: `${r}.sld` } });

              const controller = new AbortController();
              const timeoutId = setTimeout(() => controller.abort(), connectionTimeout);

              fetch(`${GEOSERVER_API}/rest/layers/${layer}/styles`, {
                headers: { Authorization: GEOSERVER_BEARER, 'Content-Type': 'application/json' },
                method: 'POST',
                body,
                signal: controller.signal,
              })
                .then((res) => {
                  clearTimeout(timeoutId);
                  return res.text();
                })
                .catch((error) => {
                  clearTimeout(timeoutId);
                  logger.error('Error adding style to layer', error);
                });
            }

            return (await response.text() as string);
          },
        }),

      }),
    deleteStyleAndUnlink: builder.mutation<void, { layerName: string; styleName: string }>({
      invalidatesTags: ['Style'],
      async queryFn({ layerName, styleName }, _api, _opts, baseQuery) {
        const getLayer = await baseQuery({
          url: `/rest/layers/${layerName}.json`,
          method: 'GET',
        });

        if (getLayer.error) return { error: getLayer.error };

        const { layer } = getLayer.data as {layer: GeoLayer & {styles: {style: Style[]}}};

        const newStyles = (layer.styles.style || []).filter(
          (s: any) => s.name !== styleName,
        );

        // 4) PUT actualizado
        const putLayer = await baseQuery({
          url: `/rest/layers/${layerName}`,
          method: 'PUT',
          body: {
            layer: {
              styles: { style: newStyles },
            },
          },
          headers: { 'Content-Type': 'application/json' },
        });

        if (putLayer.error) return { error: putLayer.error };

        const deleteStyle = await baseQuery({
          url: `/rest/styles/${styleName}?purge=true`,
          method: 'DELETE',
        });

        if (deleteStyle.error) return { error: deleteStyle.error };

        return { data: undefined };
      },
    }),

  }),
});

export const {
  useGetWorkspacesQuery,
  useGetStylesQuery,
  useGetLayerStylesQuery,
  useUpdateLayerStyleMutation,
  useCreateLayerStyleMutation,
  useLazyGetWorkspacesQuery,
  useDeleteStyleAndUnlinkMutation,
} = api;
export default api;
